/* eslint-disable @typescript-eslint/no-explicit-any -- react-hook-form useFieldArray/Control types require any for array field names */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { hackApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import type {
  Hack,
  HackRequest,
  HackCategory,
  HackType,
  HackComplexity,
  HackStatus,
  VerificationStatus,
  GenerationSource,
  TargetAudience,
} from '@/types/learningManagement';

const hackSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  description: z.string().min(0).max(2000, 'Description must be less than 2000 characters'),
  skillId: z.string().min(1, 'Skill ID is required'),
  content: z.string().min(0).max(5000, 'Content must be less than 5000 characters'),
  summary: z.string().max(500).optional(),
  problemStatement: z.string().optional(),
  solution: z.string().optional(),
  category: z.enum(['PRODUCTIVITY', 'EFFICIENCY', 'AUTOMATION', 'SHORTCUT', 'OPTIMIZATION', 'WORKFLOW', 'TIME_SAVING', 'LEARNING_ACCELERATOR', 'PROBLEM_SOLVING', 'CREATIVE', 'TECHNICAL', 'BEHAVIORAL']),
  hackType: z.enum(['PRODUCTIVITY', 'EFFICIENCY', 'AUTOMATION', 'SHORTCUT', 'OPTIMIZATION', 'WORKFLOW', 'TIME_SAVING', 'LEARNING_ACCELERATOR', 'PROBLEM_SOLVING', 'CREATIVE', 'TECHNICAL', 'BEHAVIORAL']),
  subCategory: z.string().max(100).optional(),
  tags: z.array(z.string().max(50)).optional(),
  complexity: z.enum(['SIMPLE', 'MODERATE', 'COMPLEX', 'ADVANCED', 'EXPERT_LEVEL']),
  status: z.enum(['DRAFT', 'ACTIVE', 'FEATURED', 'TRENDING', 'ARCHIVED', 'UNDER_REVIEW', 'DEPRECATED', 'COMPLETED', 'IN_PROGRESS', 'NOT_STARTED']),
  implementationSteps: z.array(z.string()),
  requiredTools: z.array(z.string()),
  prerequisites: z.array(z.string()),
  implementationTimeframe: z.string().optional(),
  effectivenessRating: z.number().min(0).max(100).optional(),
  popularityScore: z.number().min(0).max(100).optional(),
  successRate: z.number().min(0).max(100).optional(),
  targetAudience: z.enum(['EVERYONE', 'BEGINNERS', 'INTERMEDIATE', 'ADVANCED', 'PROFESSIONALS', 'DEVELOPERS', 'MANAGERS', 'STUDENTS', 'SPECIFIC_ROLE']).optional(),
  useCases: z.array(z.string()),
  benefits: z.array(z.string()),
  risksLimitations: z.array(z.string()),
  examples: z.array(z.string()),
  resources: z.array(z.string()),
  verificationStatus: z.enum(['UNVERIFIED', 'COMMUNITY_TESTED', 'EXPERT_VERIFIED', 'OFFICIALLY_APPROVED', 'DEPRECATED']).optional(),
  personalizationParams: z.record(z.string(), z.unknown()).optional(),
  hackGenerated: z.enum(['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED']),
  authorInfo: z.object({
    authorId: z.string().optional(),
    authorName: z.string().optional(),
    authorEmail: z.string().email().optional(),
  }).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

type HackFormData = z.infer<typeof hackSchema>;

const CATEGORIES: HackCategory[] = ['PRODUCTIVITY', 'EFFICIENCY', 'AUTOMATION', 'SHORTCUT', 'OPTIMIZATION', 'WORKFLOW', 'TIME_SAVING', 'LEARNING_ACCELERATOR', 'PROBLEM_SOLVING', 'CREATIVE', 'TECHNICAL', 'BEHAVIORAL'];
const HACK_TYPES: HackType[] = ['PRODUCTIVITY', 'EFFICIENCY', 'AUTOMATION', 'SHORTCUT', 'OPTIMIZATION', 'WORKFLOW', 'TIME_SAVING', 'LEARNING_ACCELERATOR', 'PROBLEM_SOLVING', 'CREATIVE', 'TECHNICAL', 'BEHAVIORAL'];
const COMPLEXITIES: HackComplexity[] = ['SIMPLE', 'MODERATE', 'COMPLEX', 'ADVANCED', 'EXPERT_LEVEL'];
const STATUSES: HackStatus[] = ['DRAFT', 'ACTIVE', 'FEATURED', 'TRENDING', 'ARCHIVED', 'UNDER_REVIEW', 'DEPRECATED', 'COMPLETED', 'IN_PROGRESS', 'NOT_STARTED'];
const VERIFICATION_STATUSES: VerificationStatus[] = ['UNVERIFIED', 'COMMUNITY_TESTED', 'EXPERT_VERIFIED', 'OFFICIALLY_APPROVED', 'DEPRECATED'];
const TARGET_AUDIENCES: TargetAudience[] = ['EVERYONE', 'BEGINNERS', 'INTERMEDIATE', 'ADVANCED', 'PROFESSIONALS', 'DEVELOPERS', 'MANAGERS', 'STUDENTS', 'SPECIFIC_ROLE'];
const GENERATION_SOURCES: GenerationSource[] = ['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED'];

export const HackFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [skills, setSkills] = useState<Array<{ id?: string; name: string }>>([]);
  const { hasPermission } = useAdminAuth();

  const form = useForm<HackFormData>({
    resolver: zodResolver(hackSchema) as any,
    defaultValues: {
      title: '',
      description: '',
      skillId: '',
      content: '',
      summary: '',
      problemStatement: '',
      solution: '',
      category: 'PRODUCTIVITY',
      hackType: 'PRODUCTIVITY',
      subCategory: '',
      complexity: 'SIMPLE',
      status: 'DRAFT',
      tags: [],
      implementationSteps: [],
      requiredTools: [],
      prerequisites: [],
      implementationTimeframe: '',
      effectivenessRating: undefined,
      popularityScore: undefined,
      successRate: undefined,
      targetAudience: undefined,
      useCases: [],
      benefits: [],
      risksLimitations: [],
      examples: [],
      resources: [],
      verificationStatus: undefined,
      personalizationParams: undefined,
      hackGenerated: 'USER_CUSTOMIZED',
      authorInfo: undefined,
      metadata: undefined,
    },
  });

  const { fields: stepFields, append: appendStep, remove: removeStep } = useFieldArray({
    control: form.control as any,
    name: 'implementationSteps' as any,
  });

  const { fields: toolFields, append: appendTool, remove: removeTool } = useFieldArray({
    control: form.control as any,
    name: 'requiredTools' as any,
  });

  const { fields: prerequisiteFields, append: appendPrerequisite, remove: removePrerequisite } = useFieldArray({
    control: form.control as any,
    name: 'prerequisites' as any,
  });

  const { fields: useCaseFields, append: appendUseCase, remove: removeUseCase } = useFieldArray({
    control: form.control as any,
    name: 'useCases' as any,
  });

  const { fields: benefitFields, append: appendBenefit, remove: removeBenefit } = useFieldArray({
    control: form.control as any,
    name: 'benefits' as any,
  });

  const { fields: riskFields, append: appendRisk, remove: removeRisk } = useFieldArray({
    control: form.control as any,
    name: 'risksLimitations' as any,
  });

  const { fields: exampleFields, append: appendExample, remove: removeExample } = useFieldArray({
    control: form.control as any,
    name: 'examples' as any,
  });

  const { fields: resourceFields, append: appendResource, remove: removeResource } = useFieldArray({
    control: form.control as any,
    name: 'resources' as any,
  });

  useEffect(() => {
    // Load skills for dropdown
    skillApi.list({ page: 0, size: 1000 }).then((response) => {
      setSkills((response.content || []).map((s) => ({ id: s.id, name: s.name })));
    }).catch((error) => {
      console.error('Error loading skills:', error);
    });
  }, []);

  // Fetch hack data for edit mode
  const {
    data: _hack,
    isLoading: isLoadingHack,
    error: hackError,
  } = useEntity<Hack>({
    fetchFn: (hackId: string) => hackApi.getById(hackId),
    entityId: id,
    entityName: 'hack',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        title: data.title,
        description: data.description || '',
        skillId: data.skillId || '',
        content: data.content,
        summary: data.summary || '',
        problemStatement: data.problemStatement || '',
        solution: data.solution || '',
        category: data.category,
        hackType: data.hackType,
        subCategory: data.subCategory || '',
        complexity: data.complexity,
        status: data.status,
        tags: data.tags || [],
        implementationSteps: data.implementationSteps || [],
        requiredTools: data.requiredTools || [],
        prerequisites: data.prerequisites || [],
        implementationTimeframe: data.implementationTimeframe || '',
        effectivenessRating: data.effectivenessRating,
        popularityScore: data.popularityScore,
        successRate: data.successRate,
        targetAudience: data.targetAudience as HackFormData['targetAudience'],
        useCases: data.useCases || [],
        benefits: data.benefits || [],
        risksLimitations: data.risksLimitations || [],
        examples: data.examples || [],
        resources: data.resources || [],
        verificationStatus: data.verificationStatus,
        personalizationParams: data.personalizationParams,
        hackGenerated: data.hackGenerated,
        authorInfo: data.authorInfo,
        metadata: data.metadata,
      } as HackFormData);
    },
    onError: () => {
      navigate('/admin/hacks');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: HackFormData) => {
      const payload: HackRequest = {
        ...data,
        tags: data.tags || [],
      };
      return await hackApi.create(payload);
    },
    entityName: 'hack',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/hacks');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: HackFormData) => {
      if (!id) throw new Error('Hack ID is required');
      const payload: HackRequest = {
        ...data,
        tags: data.tags || [],
      };
      return await hackApi.update(id, payload);
    },
    entityName: 'hack',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/hacks');
    },
  });

  const onSubmit = async (data: HackFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingHack;
  const mutation = isEdit ? updateMutation : createMutation;

  const isSystemDefined = form.watch('hackGenerated') === 'SYSTEM_DEFINED';
  const canModifyGeneration = !isEdit || !isSystemDefined || hasPermission('system_admin', 'hack');

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading hack..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && hackError) {
    return <ErrorState error={hackError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/hacks">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Hack' : 'Create Hack'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update hack information' : 'Create a new hack'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the hack</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter hack title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter hack description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter hack content"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Summary</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter hack summary (max 500 characters)" {...field} />
                    </FormControl>
                    <FormDescription>Brief summary of the hack</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="skillId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Skill ID *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select skill" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {skills.map((skill) => (
                          <SelectItem key={skill.id} value={skill.id || ''}>
                            {skill.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>Select the skill this hack is associated with</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="problemStatement"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Problem Statement</FormLabel>
                      <FormControl>
                        <textarea
                          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          placeholder="Describe the problem this hack solves"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="solution"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Solution</FormLabel>
                      <FormControl>
                        <textarea
                          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          placeholder="Describe the solution"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="hackType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hack Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select hack type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {HACK_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="complexity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Complexity *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select complexity" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COMPLEXITIES.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="subCategory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sub Category</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter sub category (optional)" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="targetAudience"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target Audience</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select target audience" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TARGET_AUDIENCES.map((audience) => (
                            <SelectItem key={audience} value={audience}>
                              {audience.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="verificationStatus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Verification Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select verification status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {VERIFICATION_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="effectivenessRating"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Effectiveness Rating (0-100)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          placeholder="0-100"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="implementationTimeframe"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Implementation Timeframe</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., 1 week, 2 days" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Implementation Steps</Label>
                  <div className="space-y-2 mt-2">
                    {stepFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder={`Step ${index + 1}`}
                          value={form.watch(`implementationSteps.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('implementationSteps') || [];
                            current[index] = e.target.value;
                            form.setValue('implementationSteps', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeStep(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendStep('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Step
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Required Tools</Label>
                  <div className="space-y-2 mt-2">
                    {toolFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter tool name"
                          value={form.watch(`requiredTools.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('requiredTools') || [];
                            current[index] = e.target.value;
                            form.setValue('requiredTools', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeTool(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendTool('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Tool
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Prerequisites</Label>
                  <div className="space-y-2 mt-2">
                    {prerequisiteFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter prerequisite"
                          value={form.watch(`prerequisites.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('prerequisites') || [];
                            current[index] = e.target.value;
                            form.setValue('prerequisites', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removePrerequisite(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendPrerequisite('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Prerequisite
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Use Cases</Label>
                  <div className="space-y-2 mt-2">
                    {useCaseFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter use case"
                          value={form.watch(`useCases.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('useCases') || [];
                            current[index] = e.target.value;
                            form.setValue('useCases', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeUseCase(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendUseCase('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Use Case
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Benefits</Label>
                  <div className="space-y-2 mt-2">
                    {benefitFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter benefit"
                          value={form.watch(`benefits.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('benefits') || [];
                            current[index] = e.target.value;
                            form.setValue('benefits', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeBenefit(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendBenefit('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Benefit
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Risks & Limitations</Label>
                  <div className="space-y-2 mt-2">
                    {riskFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter risk or limitation"
                          value={form.watch(`risksLimitations.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('risksLimitations') || [];
                            current[index] = e.target.value;
                            form.setValue('risksLimitations', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeRisk(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendRisk('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Risk/Limitation
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Examples</Label>
                  <div className="space-y-2 mt-2">
                    {exampleFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter example"
                          value={form.watch(`examples.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('examples') || [];
                            current[index] = e.target.value;
                            form.setValue('examples', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeExample(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendExample('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Example
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Resources</Label>
                  <div className="space-y-2 mt-2">
                    {resourceFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter resource URL or description"
                          value={form.watch(`resources.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('resources') || [];
                            current[index] = e.target.value;
                            form.setValue('resources', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeResource(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendResource('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Resource
                    </Button>
                  </div>
                </div>
              </div>

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter tags separated by commas"
                        value={field.value?.join(', ') || ''}
                        onChange={(e) => {
                          const tags = e.target.value
                            .split(',')
                            .map((t) => t.trim())
                            .filter(Boolean);
                          field.onChange(tags);
                        }}
                      />
                    </FormControl>
                    <FormDescription>Separate multiple tags with commas</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hackGenerated"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Generation Source *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={!canModifyGeneration}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select generation source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {GENERATION_SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {!canModifyGeneration && 'Cannot change generation source for system-defined hacks'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/hacks">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default HackFormPage;
